import React from "react";
import {
  MAX_PANORAMA_MARGIN_PERCENT,
  PanoramaAspectRatio,
} from "../../shared/panorama";

export function PanoramaPreview({
  sourceUrl,
  sourceAlt,
  sourceWidth,
  sourceHeight,
  includeSlices,
  sliceCount,
  includeBackground,
  backgroundColor,
  horizontalMargin,
  verticalMargin,
  aspectRatio,
  backgroundPosition,
}: {
  sourceUrl: string;
  sourceAlt: string;
  sourceWidth: number;
  sourceHeight: number;
  includeSlices: boolean;
  sliceCount: number;
  includeBackground: boolean;
  backgroundColor: "black" | "white";
  horizontalMargin: number;
  verticalMargin: number;
  aspectRatio: PanoramaAspectRatio;
  backgroundPosition: "before" | "after";
}) {
  const slices = includeSlices
    ? Array.from({ length: sliceCount }, (_, index) => ({
        kind: "slice" as const,
        index,
      }))
    : [];
  const background = includeBackground ? [{ kind: "background" as const }] : [];
  const items =
    backgroundPosition === "before"
      ? [...background, ...slices]
      : [...slices, ...background];

  return (
    <div className="card bg-base-100">
      <div className="card-body">
        <div>
          <h3 className="font-semibold">Output preview</h3>
          <p className="text-sm text-base-content/70">
            Images will be generated in this order. The preview uses the
            thumbnail; full-resolution output retains the source HDR data.
          </p>
        </div>

        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <div className="flex min-w-max items-start gap-3 px-1 pb-2">
              {items.map((item) =>
                item.kind === "slice" ? (
                  <SlicePreview
                    key={`slice-${item.index}`}
                    sourceUrl={sourceUrl}
                    sourceAlt={sourceAlt}
                    sourceWidth={sourceWidth}
                    sourceHeight={sourceHeight}
                    sliceCount={sliceCount}
                    sliceIndex={item.index}
                  />
                ) : (
                  <BackgroundPreview
                    key="background"
                    sourceUrl={sourceUrl}
                    sourceAlt={sourceAlt}
                    color={backgroundColor}
                    horizontalMargin={horizontalMargin}
                    verticalMargin={verticalMargin}
                    aspectRatio={aspectRatio}
                  />
                ),
              )}
            </div>
          </div>
        ) : (
          <div className="alert">Select at least one output to preview.</div>
        )}
      </div>
    </div>
  );
}

function SlicePreview({
  sourceUrl,
  sourceAlt,
  sourceWidth,
  sourceHeight,
  sliceCount,
  sliceIndex,
}: {
  sourceUrl: string;
  sourceAlt: string;
  sourceWidth: number;
  sourceHeight: number;
  sliceCount: number;
  sliceIndex: number;
}) {
  const ratio = sourceWidth / sliceCount / sourceHeight;
  const dimensions = getPreviewDimensions(ratio);
  const ratioLabel = `${Number(ratio.toFixed(3))}:1`;

  return (
    <div className="w-44 shrink-0">
      <div className="flex h-40 items-center justify-center">
        <div
          className="relative overflow-hidden rounded-lg border border-base-300 bg-base-200 shadow-sm"
          style={dimensions}
        >
          <img
            src={sourceUrl}
            alt={`${sourceAlt}, slice ${sliceIndex + 1}`}
            className="absolute left-0 top-0 block max-w-none"
            style={{
              width: `${sliceCount * 100}%`,
              height: "100%",
              transform: `translateX(-${(sliceIndex * 100) / sliceCount}%)`,
            }}
          />
        </div>
      </div>
      <div className="mt-2 text-center text-sm text-base-content/70">
        {ratioLabel}
      </div>
    </div>
  );
}

function BackgroundPreview({
  sourceUrl,
  sourceAlt,
  color,
  horizontalMargin,
  verticalMargin,
  aspectRatio,
}: {
  sourceUrl: string;
  sourceAlt: string;
  color: "black" | "white";
  horizontalMargin: number;
  verticalMargin: number;
  aspectRatio: PanoramaAspectRatio;
}) {
  const ratio =
    aspectRatio.width > 0 && aspectRatio.height > 0
      ? aspectRatio.width / aspectRatio.height
      : 1;
  const dimensions = getPreviewDimensions(ratio);
  const marginX = clampPreviewMargin(horizontalMargin);
  const marginY = clampPreviewMargin(verticalMargin);

  return (
    <div className="w-44 shrink-0">
      <div className="flex h-40 items-center justify-center">
        <div
          className="relative overflow-hidden rounded-lg border border-base-300 shadow-sm"
          style={{
            ...dimensions,
            backgroundColor: color,
          }}
        >
          <div
            className="absolute overflow-hidden"
            style={{
              left: `${marginX}%`,
              top: `${marginY}%`,
              width: `${100 - 2 * marginX}%`,
              height: `${100 - 2 * marginY}%`,
            }}
          >
            <img
              src={sourceUrl}
              alt={`${sourceAlt} with ${color} background`}
              className="h-full w-full object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function getPreviewDimensions(ratio: number) {
  const maxSize = 160;
  const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;

  return safeRatio >= 1
    ? { width: maxSize, height: maxSize / safeRatio }
    : { width: maxSize * safeRatio, height: maxSize };
}

function clampPreviewMargin(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(MAX_PANORAMA_MARGIN_PERCENT, Math.max(0, value));
}
